import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { AlternativePageProps } from './types';
import { ArrowGlyph, delay, SectionHeading, tone } from '../primitives';
import { useReveal } from '../useReveal';

/** How to move over, step by step, each step linked to its docs page. */
export default function SwitchGuide({
  name,
  migration,
}: Pick<AlternativePageProps, 'name' | 'migration'>) {
  const { ref, state } = useReveal<HTMLOListElement>(0.2);

  return (
    <section id='switch' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={`Switching from ${name}.`}
          tail='Four steps, no code.'
          lead={migration.intro}
        />
        <ol
          ref={ref}
          data-reveal={state}
          className={clsxm(
            styles.reveal,
            'mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4',
          )}
        >
          {migration.steps.map((step, index) => (
            <li
              key={step.title}
              className={clsxm(
                styles.rv,
                'relative flex flex-col rounded-[24px] p-6',
                tone.surface,
                tone.line,
              )}
              style={delay(index * 90)}
            >
              <span className='flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-[15px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_8px_20px_-8px_rgba(79,70,229,0.7)]'>
                {index + 1}
              </span>
              <h3
                className={clsxm(
                  'mt-5 text-lg font-semibold leading-snug',
                  tone.ink,
                )}
              >
                {step.title}
              </h3>
              <p
                className={clsxm(
                  'mt-2 flex-1 text-[15px] leading-7',
                  tone.body,
                )}
              >
                {step.body}
              </p>
              {step.href && (
                <a
                  href={step.href}
                  className='mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
                >
                  How it works
                  <ArrowGlyph className='h-3.5 w-3.5' />
                </a>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
